import { Task } from './task';
import { TaskActions } from './task-actions';
import { Nullable } from 'src/core/common/domain/types/types';

/**
 * The API grants the ability to confirm the close/reject of a task through the 'confirm_close_and_reject'
 * available action. It is the only meaning this app gives to it: it is never used as a role.
 */
export const grantsConfirmAndCloseTasks = (grantedActions: Nullable<Array<TaskActions>>): boolean =>
  grantedActions !== null && grantedActions.includes(TaskActions.CONFIRM_CLOSE_AND_REJECT);

/**
 * The actions a user can still perform on a task, given the actions the API grants them (master data
 * available actions) and the current task status.
 *
 * | status             | without confirm ability     | with confirm ability |
 * |--------------------|-----------------------------|----------------------|
 * | pending            | START, CLOSE, REJECT        | same                 |
 * | in_progress        | STOP, CLOSE, REJECT         | same                 |
 * | closed             | —                           | CONFIRM_CLOSE        |
 * | rejected           | —                           | CONFIRM_REJECT       |
 * | closed_confirmed   | —                           | —                    |
 * | rejected_confirmed | —                           | —                    |
 *
 * START, CLOSE and REJECT are only available when granted. STOP is granted together with START: the API
 * never sends 'stop' as an available action.
 */
export class TaskAvailableActions {
  private constructor(private readonly actions: Set<TaskActions>) {}

  static for(task: Task, grantedActions: Nullable<Array<TaskActions>>): TaskAvailableActions {
    // cover the case of miliseconds after logout where master data is deleted
    if (!grantedActions) {
      return new TaskAvailableActions(new Set());
    }

    const actions = new Set<TaskActions>();
    const canStartAndStop = grantedActions.includes(TaskActions.START);

    if (task.status.isPending() && canStartAndStop) {
      actions.add(TaskActions.START);
    }

    if (task.status.isInProgress() && canStartAndStop) {
      actions.add(TaskActions.STOP);
    }

    if (task.status.isPending() || task.status.isInProgress()) {
      if (grantedActions.includes(TaskActions.CLOSE)) {
        actions.add(TaskActions.CLOSE);
      }
      if (grantedActions.includes(TaskActions.REJECT)) {
        actions.add(TaskActions.REJECT);
      }
    }

    if (grantsConfirmAndCloseTasks(grantedActions)) {
      if (task.status.isClosed()) {
        actions.add(TaskActions.CONFIRM_CLOSE);
      }
      if (task.status.isRejected()) {
        actions.add(TaskActions.CONFIRM_REJECT);
      }
    }

    return new TaskAvailableActions(actions);
  }

  includes(action: TaskActions): boolean {
    return this.actions.has(action);
  }

  isEmpty(): boolean {
    return this.actions.size === 0;
  }

  toArray(): Array<TaskActions> {
    return Array.from(this.actions);
  }
}

/**
 * Whether a task opened on its own (from a push or in-app notification) is still worth showing: a task
 * that is already done and leaves nothing to do must not be opened. A task that is not done yet is always
 * shown, even when the user was granted no action at all: it can still be read.
 */
export const canTaskBeOpenedFromNotification = (task: Task, grantedActions: Nullable<Array<TaskActions>>): boolean => {
  if (task.status.isPending() || task.status.isInProgress()) {
    return true;
  }

  if (task.isInFinalState()) {
    return false;
  }

  return !TaskAvailableActions.for(task, grantedActions).isEmpty();
};
